import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { createNameId } from 'mnemonic-id';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { DateTime } from 'luxon';
import Button from '@/components/core/Button';
import CheckBox from '@/components/core/Checkbox';
import IconButton from '@/components/core/IconButton';
import Input from '@/components/core/Input';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { cn } from '@/helpers/classnames';
import { groupActiveErrorReportsByEntityId } from '@/helpers/error_report_helpers';
import { IconCheck, IconHelp, IconTrash } from '@/icons';
import { dateFormat } from '../../helpers/datetime_helpers';
import { useEntityTypes } from '../../hooks/useEntityTypes';
import { useFilterSets } from '../../hooks/useFilterSets';
import { useEntities } from '../../hooks/useEntities';
import { useErrorReports } from '../../hooks/useErrorReports';
import { useRoutes } from '../../hooks/useRoutes';
import { filterSetToExecutionFilters } from '../../helpers/filter_helpers';
import useEditWorkOrderDialogStore from '../../store/editWorkOrderDialog';
import {
  useWorkOrderMutations,
  workOrderToRequest,
} from '../../hooks/useWorkOrders';
import { useContractors } from '../../hooks/useContractors';
import useGuideStore from '../../store/guide';
import { useWorkOrderInstructions } from '../../hooks/useWorkOrderInstructions';
import { calculateFilteredEntities } from '../../hooks/useFiltering';
import { useEntitySchema } from '../../hooks/useEntitySchema';
import { useApiClient } from '../../hooks/useApiClient';
import TagSelector from '../Tags/TagSelector';
import UnsavedChangesDialog from '../UnsavedChangesDialog';
import Tooltip from '../Tooltip';
import { DialogWindow } from '../DialogWindow';
import { EntitySearch } from '../EntitySearch';

const VISIBLE_ENTITIES_LIMIT = 100;

const createDefaultWorkOrder = (entityTypeId: string): WorkOrderRequest => {
  return {
    entity_type_id: entityTypeId,
    state: 'created',
    contractors: [],
    entity_changesets: {},
    description: '',
    tags: [],
    title: createNameId(),
    entities: [],
  };
};

const messages = defineMessages({
  dialogTitle: {
    id: 'edit_workorder_dialog.edit_workorder',
    defaultMessage: 'Edit workorder',
  },
  help: {
    id: 'help',
    defaultMessage: 'Help',
  },
  remove: {
    id: 'remove',
    defaultMessage: 'Remove',
  },
  title: {
    id: 'edit_workorder_dialog.title',
    defaultMessage: 'Title',
  },
  dueDate: {
    id: 'edit_workorder_dialog.due_date',
    defaultMessage: 'Due date',
  },
  contractor: {
    id: 'edit_workorder_dialog.contractor',
    defaultMessage: 'Contractor',
  },
  noContractor: {
    id: 'edit_workorder_dialog.no_contractor',
    defaultMessage: 'No contractor',
  },
  autoAssign: {
    id: 'edit_workorder_dialog.auto_assign',
    defaultMessage: 'Auto-assign',
  },
  filterset: {
    id: 'edit_workorder_dialog.filterset',
    defaultMessage: 'Filterset',
  },
  addStopsFromFilterset: {
    id: 'edit_workorder_dialog.add_stops_from_a_filterset',
    defaultMessage: 'Add stops from a filterset',
  },
  workInstructionsLabel: {
    id: 'edit_workorder_dialog.work_instructions_label',
    defaultMessage: 'Work instructions',
  },
  workInstructions: {
    id: 'edit_workorder_dialog.work_instructions',
    defaultMessage: 'Enter work instructions here...',
  },
  attachmentMessage: {
    id: 'edit_workorder_dialog.attachment_message',
    defaultMessage: 'Message for attachments',
  },
  attachmentMessagePlaceholder: {
    id: 'edit_workorder_dialog.attachment_message_placeholder',
    defaultMessage: 'Comment for attachments...',
  },
  noTemplates: {
    id: 'no_templates_created',
    defaultMessage: 'No templates created',
  },
  searchStops: {
    id: 'edit_workorder_dialog.search_stops',
    defaultMessage: 'Search for a stop to add...',
  },
  noFiltersets: {
    id: 'edit_workorder_dialog.no_filtersets',
    defaultMessage: 'No filtersets created',
  },
});

const EditWorkOrderDialog: React.FC = () => {
  const { data: schema } = useEntitySchema();
  const { goToWorkOrderDetails } = useRoutes();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { stop } = useEntityTypes();
  const { openGuide } = useGuideStore();
  const { list: entities, lookup: entitiesLookup } = useEntities();
  const { list: filterSets } = useFilterSets();
  const { list: instructions } = useWorkOrderInstructions();
  const { list: errorReports } = useErrorReports();
  const {
    show: showEditWorkOrderDialog,
    close: closeEditWorkOrderDialog,
    selectedEntityIds,
    setSelectedEntityIds,
    workOrderToEdit,
  } = useEditWorkOrderDialogStore();
  const {
    create: { mutateAsync: createWorkOrder, isLoading: isCreating },
    update: { mutateAsync: updateWorkOrder },
  } = useWorkOrderMutations();
  const { list: contractors } = useContractors();
  const apiClient = useApiClient();

  const [confirmDirty, setConfirmDirty] = useState<boolean>(false);
  const [changes, setChanges] = useState<boolean>(false);
  const [autoAssign, setAutoAssign] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileMessage, setFileMessage] = useState<string>('');

  const [localWorkOrder, setLocalWorkOrder] = useState<WorkOrderRequest>(
    workOrderToEdit
      ? workOrderToRequest(workOrderToEdit)
      : createDefaultWorkOrder(stop?.id || ''),
  );

  const activeErrorReportsByEntityId = useMemo(
    () => groupActiveErrorReportsByEntityId(errorReports),
    [errorReports],
  );

  const contractorOptions = useMemo<OptionsType[]>(
    () => contractors.map((c) => ({ id: c.id, label: c.name })),
    [contractors],
  );
  const selectedContractorOptions = useMemo<OptionsType[]>(
    () =>
      contractorOptions.filter((option) =>
        localWorkOrder.contractors.includes(option.id),
      ),
    [contractorOptions, localWorkOrder.contractors],
  );
  const filterSetsOptions = useMemo<OptionsType[]>(
    () => filterSets.map((f) => ({ id: f.id, label: f.name })),
    [filterSets],
  );
  const instructionsOptions = useMemo<OptionsType[]>(
    () => instructions.map((i) => ({ id: i.id, label: i.title })),
    [instructions],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setFiles([
        ...files,
        ...acceptedFiles.filter(
          (newFile) => !files.some((f) => f.name === newFile.name),
        ),
      ]);
    },
    [files],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ onDrop });

  const onRemoveFile = useCallback(
    (file: File) => {
      setFiles([...files.filter((f) => f !== file)]);
    },
    [files],
  );

  useEffect(() => {
    setLocalWorkOrder(
      workOrderToEdit
        ? workOrderToRequest(workOrderToEdit)
        : createDefaultWorkOrder(stop?.id || ''),
    );
  }, [stop, workOrderToEdit]);

  const setLocalWorkOrderAndFlagChanges = useCallback(
    (wo: WorkOrderRequest) => {
      setChanges(true);
      setLocalWorkOrder(wo);
    },
    [setLocalWorkOrder],
  );

  const updateSelectedEntityIds = useCallback(
    (entityIds: string[]) => {
      setChanges(true);
      setSelectedEntityIds(entityIds);
    },
    [setSelectedEntityIds],
  );

  const onAddFilterSet = useCallback(
    (filterSetId: string) => {
      if (!schema) {
        return;
      }
      const filterSet = filterSets.find((f) => f.id === filterSetId);
      if (!filterSet) {
        return;
      }
      const results = calculateFilteredEntities(
        entities,
        entitiesLookup,
        filterSetToExecutionFilters(filterSet),
        schema,
        true,
      );
      const seenIds: string[] = [];
      const newEntityIds = [
        ...selectedEntityIds,
        ...results.final.map((e) => e.id),
      ].filter((entityId) => {
        if (seenIds.includes(entityId)) {
          return false;
        }
        seenIds.push(entityId);
        return true;
      });
      updateSelectedEntityIds(newEntityIds);
    },
    [
      entities,
      entitiesLookup,
      filterSets,
      schema,
      selectedEntityIds,
      updateSelectedEntityIds,
    ],
  );

  const closeDialog = useCallback(() => {
    setLocalWorkOrder(createDefaultWorkOrder(stop?.id || ''));
    setConfirmDirty(false);
    setChanges(false);
    setAutoAssign(false);
    setFiles([]);
    setFileMessage('');
    closeEditWorkOrderDialog();
  }, [closeEditWorkOrderDialog, stop]);

  const handleSave = useCallback(
    (open?: boolean) => {
      const localWOWithEntities = {
        ...localWorkOrder,
        entities: selectedEntityIds,
      };
      if (workOrderToEdit) {
        updateWorkOrder({
          id: workOrderToEdit.id,
          request: localWOWithEntities,
        })
          .then(() => {
            closeDialog();
            open && goToWorkOrderDetails(workOrderToEdit.id);
            enqueueSnackbar(
              <FormattedMessage
                defaultMessage="Work order updated"
                id="edit_workorder_dialog.work_order_updated"
              />,
              { variant: 'success' },
            );
          })
          .catch((err) => {
            throw err;
          });
      } else {
        createWorkOrder({
          request: localWOWithEntities,
          autoAssign: autoAssign,
        })
          .then(async (workOrders) => {
            // If files are attached, create a comment event for each created work order
            if (files.length > 0) {
              const commentText =
                fileMessage ||
                intl.formatMessage({
                  id: 'edit_workorder_dialog.attached_files',
                  defaultMessage: 'Attached files',
                });
              const fileUploadPromises = workOrders.map((workOrder) =>
                apiClient.workOrders.createEvent(
                  workOrder.id,
                  {
                    type: 'comment',
                    data: {
                      text: commentText,
                    },
                  },
                  files,
                ),
              );
              await Promise.all(fileUploadPromises);
            }
            closeDialog();
            open &&
              workOrders.length === 1 &&
              goToWorkOrderDetails(workOrders[0].id);
            enqueueSnackbar(
              <FormattedMessage
                defaultMessage="Work order created"
                id="edit_workorder_dialog.work_order_created"
              />,
              { variant: 'success' },
            );
          })
          .catch((err) => {
            throw err;
          });
      }
    },
    [
      localWorkOrder,
      selectedEntityIds,
      workOrderToEdit,
      updateWorkOrder,
      closeDialog,
      goToWorkOrderDetails,
      enqueueSnackbar,
      createWorkOrder,
      autoAssign,
      files,
      fileMessage,
      apiClient,
      intl,
    ],
  );

  const tryClose = useCallback(() => {
    if (changes) {
      setConfirmDirty(true);
    } else {
      closeDialog();
    }
  }, [changes, closeDialog]);

  const onLoadInstruction = useCallback(
    (instructionId: string) => {
      const instruction = instructions.find((i) => i.id === instructionId);
      if (!instruction) {
        return;
      }
      setLocalWorkOrderAndFlagChanges({
        ...localWorkOrder,
        description: localWorkOrder.description
          ? localWorkOrder.description + `\n` + instruction.texts.join(`\n`)
          : instruction.texts.join(`\n`),
        tags: Array.from(
          new Set([...localWorkOrder.tags, ...(instruction.tags || [])]),
        ),
      });
    },
    [instructions, localWorkOrder, setLocalWorkOrderAndFlagChanges],
  );

  const createAndOpenDisabled =
    selectedEntityIds.length > VISIBLE_ENTITIES_LIMIT || autoAssign;

  const content = (
    <div className="grid grid-cols-2 gap-x-8 gap-y-10 pt-6">
      <div className="flex items-center gap-2">
        <Input
          id="edit-workorder-title"
          label={intl.formatMessage(messages.title)}
          value={localWorkOrder.title}
          onChange={(event) =>
            setLocalWorkOrderAndFlagChanges({
              ...localWorkOrder,
              title: event.target.value,
            })
          }
        />
        {localWorkOrder.id && (
          <span className="whitespace-nowrap text-text-700">
            {`#${localWorkOrder.id.slice(0, 6)}`}
          </span>
        )}
      </div>

      <div className="">
        <Input
          id="edit-workorder-due-date"
          type="date"
          label={intl.formatMessage(messages.dueDate)}
          value={localWorkOrder.due_at?.toFormat(dateFormat) ?? ''}
          onChange={(event) => {
            const dueAt = DateTime.fromFormat(event.target.value, dateFormat);
            setLocalWorkOrderAndFlagChanges({
              ...localWorkOrder,
              due_at: dueAt.isValid ? dueAt : undefined,
            });
          }}
        />
      </div>

      <div className="flex items-center gap-4">
        <Dropdown
          id="edit-workorder-contractors"
          multiSelect
          label={intl.formatMessage(messages.contractor)}
          placeholder={intl.formatMessage(messages.noContractor)}
          options={contractorOptions}
          value={selectedContractorOptions}
          onValueChange={(values) =>
            setLocalWorkOrderAndFlagChanges({
              ...localWorkOrder,
              contractors: values.map((v) => v.id),
            })
          }
        />
        {!workOrderToEdit && (
          <Tooltip
            label={
              <span className="block max-w-xs">
                <FormattedMessage
                  id="edit_workorder_dialog.auto_assign_desc"
                  defaultMessage="Automatically distribute created workorders among assigned contractors based on contractor filtersets. If no contractors are assigned, distribute among all of them."
                />
              </span>
            }
          >
            <CheckBox
              id="edit-workorder-auto-assign"
              checked={autoAssign}
              onCheckedChange={(checked) => setAutoAssign(checked === true)}
              label={intl.formatMessage(messages.autoAssign)}
              labelClassName="whitespace-nowrap"
            />
          </Tooltip>
        )}
      </div>

      <Dropdown
        id="edit-workorder-filterset"
        label={intl.formatMessage(messages.filterset)}
        options={filterSetsOptions}
        placeholder={
          filterSetsOptions.length === 0
            ? intl.formatMessage(messages.noFiltersets)
            : undefined
        }
        onValueChange={(value) => onAddFilterSet(value.id)}
        disabled={filterSetsOptions.length === 0}
      />

      <div className="flex flex-col">
        <Input
          id="edit-workorder-description"
          type="multiline"
          rows={8}
          label={intl.formatMessage(messages.workInstructionsLabel)}
          placeholder={intl.formatMessage(messages.workInstructions)}
          value={localWorkOrder.description}
          onChange={(event) =>
            setLocalWorkOrderAndFlagChanges({
              ...localWorkOrder,
              description: event.target.value,
            })
          }
        />

        <div className="mt-1 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() =>
              setLocalWorkOrderAndFlagChanges({
                ...localWorkOrder,
                description: '',
              })
            }
          >
            <FormattedMessage
              id="edit_workorder_dialog.clear"
              defaultMessage="Clear"
            />
          </Button>
          <div className="w-72">
            <Dropdown
              id="instructions-dropdown"
              options={instructionsOptions}
              value={{
                id: '',
                label: intl.formatMessage({
                  id: 'edit_workorder_dialog.load_template',
                  defaultMessage: 'Load template',
                }),
              }}
              onValueChange={(value) => onLoadInstruction(value.id)}
            />
          </div>
        </div>

        <div className="mt-4">
          <p className="font-medium text-text-900">
            <FormattedMessage
              id="edit_workorder_dialog.tags"
              defaultMessage="Tags"
            />
          </p>
          <TagSelector
            onChange={(selectedTags) =>
              setLocalWorkOrderAndFlagChanges({
                ...localWorkOrder,
                tags: selectedTags,
              })
            }
            selectedTagIds={localWorkOrder?.tags || []}
            className="w-full"
          />
        </div>

        {!workOrderToEdit && (
          <div className="mt-6">
            <p className="text-[10px] uppercase leading-4 tracking-[1.5px] text-text-700">
              <FormattedMessage
                id="edit_workorder_dialog.attachments"
                defaultMessage="Attachments"
              />
            </p>
            <div className="pt-6">
              <Input
                id="edit-workorder-attachment-message"
                type="multiline"
                rows={3}
                label={intl.formatMessage(messages.attachmentMessage)}
                placeholder={intl.formatMessage(
                  messages.attachmentMessagePlaceholder,
                )}
                value={fileMessage}
                onChange={(event) => setFileMessage(event.target.value)}
              />
            </div>
            <div
              {...getRootProps({
                className: cn(
                  'mt-1 flex cursor-pointer flex-col items-center rounded-md border-2 border-dashed border-background-400 bg-background-100 p-5 text-text-700 outline-none transition-colors',
                  isDragActive && 'border-primary-500',
                  isDragAccept && 'border-green-600',
                  isDragReject && 'border-red-600',
                ),
              })}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <FormattedMessage
                  id="edit_workorder_dialog.drop_files_here"
                  defaultMessage="Drop files here..."
                />
              ) : (
                <FormattedMessage
                  id="edit_workorder_dialog.drag_or_click"
                  defaultMessage="Drag files here, or click to select files"
                />
              )}
            </div>
            <p className="mt-2 text-sm leading-6 text-text-800">
              <FormattedMessage
                id="edit_workorder_dialog.selected_files"
                defaultMessage="{numberOfFiles, plural, =0 {No files selected} other {Selected files}}"
                values={{ numberOfFiles: files.length }}
              />
            </p>
            <div className="mt-1 flex flex-wrap gap-2">
              {files.map((file) => (
                <span
                  key={file.name}
                  className="inline-flex items-center gap-1 rounded-md border border-background-400 py-1 pl-2 pr-1"
                >
                  <span className="text-base font-semibold leading-6">
                    {file.name}
                  </span>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<IconTrash />}
                    aria-label={intl.formatMessage(messages.remove)}
                    onClick={() => onRemoveFile(file)}
                  />
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <EntitySearch
        options={entities}
        onChange={updateSelectedEntityIds}
        selectedEntityIds={selectedEntityIds}
        className="max-h-[600px]"
        errorReportsByEntityId={activeErrorReportsByEntityId}
      />
    </div>
  );

  const createAndOpenButton = (
    <Button
      variant="filled"
      disabled={createAndOpenDisabled}
      onClick={() => handleSave(true)}
      startIcon={<IconCheck />}
    >
      <FormattedMessage
        id="edit_workorder_dialog.create_and_open_workorder"
        defaultMessage="Create and open workorder"
      />
    </Button>
  );

  const footer = (
    <>
      <Button variant="ghost" onClick={tryClose}>
        <FormattedMessage
          id="edit_workorder_dialog.cancel"
          defaultMessage="Cancel"
        />
      </Button>
      <Button
        variant="filled"
        className="min-w-[180px]"
        loading={isCreating}
        disabled={autoAssign && selectedEntityIds.length === 0}
        onClick={() => handleSave()}
        startIcon={<IconCheck />}
      >
        {workOrderToEdit ? (
          <FormattedMessage
            id="edit_workorder_dialog.save_workorder"
            defaultMessage="Save workorder"
          />
        ) : (
          <FormattedMessage
            id="edit_workorder_dialog.create_workorder"
            defaultMessage="Create workorder"
          />
        )}
      </Button>
      {!workOrderToEdit &&
        (createAndOpenDisabled ? (
          <Tooltip
            label={
              <span className="block max-w-xs">
                <FormattedMessage
                  id="edit_workorder_dialog.create_and_open_disabled_help"
                  defaultMessage="Disabled when request might lead to the creation of more than one workorder."
                />
              </span>
            }
          >
            <span className="inline-flex">{createAndOpenButton}</span>
          </Tooltip>
        ) : (
          createAndOpenButton
        ))}
    </>
  );

  return (
    <>
      <UnsavedChangesDialog
        open={confirmDirty}
        onOk={() => {
          setConfirmDirty(false);
          setChanges(false);
          closeDialog();
        }}
        onCancel={() => setConfirmDirty(false)}
      />
      <DialogWindow
        open={showEditWorkOrderDialog}
        onOpenChange={(open) => !open && tryClose()}
        title={intl.formatMessage(messages.dialogTitle)}
        headerActions={
          <IconButton
            variant="ghost"
            icon={<IconHelp />}
            aria-label={intl.formatMessage(messages.help)}
            onClick={() => openGuide('EditWorkOrderDialog')}
          />
        }
        content={content}
        footer={footer}
        contentClassName="overflow-y-auto"
      />
    </>
  );
};

export default EditWorkOrderDialog;
